// ─── Relatório Especializado: Teste de Fumaça / Inspeção de Rede ─────────────
// Usado para os.tipoServico === 'Teste de Fumaça' | 'Inspeção de Rede'
// empresa → 'pg' | 'norbrasil'

const MAPBOX_TOKEN = 'pk.eyJ1Ijoic2ltbW1wbGUiLCJhIjoiY2wxeG1hd24xMDEzYzNrbWs5emFkdm16ZiJ9.q9s0sSKQFFaT9fyrC-7--g';

function buildMapboxStaticUrl(nos, arestas, style, w = 640, h = 360) {
  const positioned = (nos || []).filter(n => n.lat && n.lng);
  if (!positioned.length) return null;
  const nosMap = Object.fromEntries((nos || []).map(n => [n._id, n]));
  const lines = (arestas || [])
    .filter(ar => { const f = nosMap[ar.de]; const t = nosMap[ar.para]; return f?.lat && f?.lng && t?.lat && t?.lng; })
    .map(ar => { const f = nosMap[ar.de]; const t = nosMap[ar.para]; return {
      type: 'Feature',
      properties: { stroke: '#3965FF', 'stroke-width': 3, 'stroke-opacity': 0.95 },
      geometry: { type: 'LineString', coordinates: [[f.lng, f.lat], [t.lng, t.lat]] },
    }; });
  const pins = positioned
    .map(n => {
      const label = encodeURIComponent(String(n.num ?? '').trim());
      return `pin-s-${label}+3965FF(${n.lng},${n.lat})`;
    })
    .join(',');
  const overlays = [];
  if (lines.length) {
    const gj = encodeURIComponent(JSON.stringify({ type: 'FeatureCollection', features: lines }));
    overlays.push(`geojson(${gj})`);
  }
  overlays.push(pins);
  const styleId = style === 'satellite' ? 'satellite-streets-v12' : 'streets-v12';
  return `https://api.mapbox.com/styles/v1/mapbox/${styleId}/static/${overlays.join(',')}/auto/${w}x${h}?padding=50&access_token=${MAPBOX_TOKEN}`;
}

function fmt(ts) {
  if (!ts) return '--';
  const d = ts.toDate ? ts.toDate() : new Date(ts);
  return d.toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' });
}
function fmtDate(ts) {
  if (!ts) return '--';
  const d = ts.toDate ? ts.toDate() : new Date(ts);
  return d.toLocaleDateString('pt-BR');
}
function v(val) { return val || '--'; }

// Dicionários de labels
const TIPO_OCC_LABEL = {
  galeria:   'Galeria de Águas Pluviais',
  boca_lobo: 'Boca de Lobo',
  boca_leao: 'Boca de Leão',
  imovel:    'Imóvel',
};

export function gerarRelatorioFumaca(os, tecnicoNome, logoUrl, { STATUS_LABELS, CHECKLIST_PADRAO, contrato }, empresa = 'pg') {
  const isPG       = empresa !== 'norbrasil';
  const cor        = isPG ? '#2b6cb0' : '#2e2b7e';
  const corLight   = isPG ? '#bee3f8' : '#eae8f8';
  const corAccent  = isPG ? '#3182ce' : '#4a47b2';
  const empresa_nome = isPG ? 'PG SOLUÇÕES INTELIGENTES' : 'NORBRASIL SERVIÇOS LTDA';
  const _contratoPart = contrato ? `Contrato ${contrato.numero} — ${contrato.cidade}` : null;
  const empresa_sub  = _contratoPart
    ? _contratoPart
    : (isPG ? 'Sistema de Gestão de Ordens de Serviço' : 'Contrato SABESP — Rede Coletora de Esgoto');

  const badgeMap = {
    aprovada:             '#c6f6d5;color:#276749',
    reprovada:            '#fed7d7;color:#9b2c2c',
    aguardando_validacao: '#feebc8;color:#7b341e',
    rascunho:             '#e2e8f0;color:#4a5568',
    enviada:              '#bee3f8;color:#2c5282',
  };
  const bc = badgeMap[os.status] || '#e2e8f0;color:#4a5568';

  const de = os.dadosEspecificos || {};
  const sings     = de.singularidades || [];
  const ocorrs    = de.ocorrencias    || [];
  const fotos_os  = os.fotos           || [];
  const mapaCampo   = de.mapaCampo || {};
  const mapaNoS      = mapaCampo.nos     || [];
  const mapaArestas  = mapaCampo.arestas || [];
  const urlRuas      = buildMapboxStaticUrl(mapaNoS, mapaArestas, 'streets',   640, 500);
  const urlSatelite  = buildMapboxStaticUrl(mapaNoS, mapaArestas, 'satellite', 640, 500);
  const fotoEnderecoInspecionado = de.fotoEnderecoInspecionado?.url ? de.fotoEnderecoInspecionado : null;
  const fotoGoogleMaps = de.fotoGoogleMaps?.url ? de.fotoGoogleMaps : null;
  const getPrevSingIndex = (i, s) => {
    if (i <= 0) return -1;
    if (s?.pvAnteriorId) {
      const idx = sings.findIndex(x => x?._id === s.pvAnteriorId);
      if (idx >= 0 && idx < i) return idx;
    }
    return i - 1;
  };
  const getPrevSingLabel = (i, s) => {
    const prevIdx = getPrevSingIndex(i, s);
    if (prevIdx < 0) return '—';
    const prev = sings[prevIdx] || {};
    const idf = prev.numeroImovel ? ` ${prev.numeroImovel}` : '';
    return `SING ${prevIdx + 1}${idf}`;
  };

  // ── Estatísticas automáticas ──────────────────────────────────────────────
  const totalSings     = sings.length;
  const singsCobertos  = sings.filter(s => s.pvCoberto || s.pvTravado).length;
  const singsInspecio  = totalSings - singsCobertos;
  const metragemTotal  = sings.reduce((a, s) => {
    const metPrincipal = parseFloat(s.metragemAoPV) || 0;
    const metAdicional = (s.ramaisAdicionais || []).reduce((sum, r) => sum + (parseFloat(r.metragem) || 0), 0);
    return a + metPrincipal + metAdicional;
  }, 0);
  const totalOcorr     = ocorrs.length;
  const singAlerta     = sings.filter(s =>
    (s.condSing    && s.condSing    !== 'Normal') ||
    (s.condBancada && s.condBancada !== 'Normal') ||
    (s.condCanaleta && s.condCanaleta !== 'Normal') ||
    (s.resultado?.length > 0 && !(s.resultado.length === 1 && s.resultado[0] === 'Normal'))
  ).length;
  const totalFotos = sings.reduce((a, s) =>
    a + (s.fotoInicio?.url ? 1 : 0) + (s.fotoFim?.url ? 1 : 0), 0
  ) + ocorrs.reduce((a, o) => a + (o.fotos?.length || 0), 0) + fotos_os.length
    + (fotoEnderecoInspecionado ? 1 : 0) + (fotoGoogleMaps ? 1 : 0);

  // ── Header da empresa (compartilhado entre páginas) ───────────────────────
  const pageHeader = `
    <div class="page-header">
      <div style="display:flex;align-items:center;gap:14px;">
        <img src="${logoUrl}" style="height:44px;object-fit:contain;" alt="Logo"/>
        <div>
          <div style="font-size:10px;color:#718096;font-weight:700;text-transform:uppercase;letter-spacing:.05em;">${empresa_nome}</div>
          <div style="font-size:10px;color:#a0aec0;">${empresa_sub}</div>
        </div>
      </div>
      <div style="text-align:right;line-height:1.6;">
        <div style="font-size:9px;color:#718096;">Desenvolvido por <strong style="color:#06b6d4;font-weight:700;">SyncWave</strong></div>
        <div style="font-size:8px;color:#a0aec0;">www.syncwave.com.br</div>
      </div>
    </div>`;

  // ── PÁGINA 1: Cabeçalho + Identificação + Localização + Resumo ───────────
  const endTrecho = [de.endereco, de.bairro, de.cidade, de.estado].filter(Boolean).join(', ');
  const endOS     = [os.logradouro, os.numero, os.bairro, os.cidade, os.uf].filter(Boolean).join(', ');

  const pagina1 = `
    <div class="page">
      ${pageHeader}
      <div class="os-title-bar">
        <div>
          <h1 style="color:${cor};font-size:20px;margin:0 0 4px;">
            Relatório fotográfico – Teste de fumaça #${v(os.numeroOS)}
          </h1>
        </div>
        <div style="text-align:right;font-size:11px;color:#718096;">
          <div>Criada em: <strong>${fmt(os.criadaEm)}</strong></div>
          <div>Enviada:   <strong>${fmt(os.atualizadaEm)}</strong></div>
          ${os.validadaEm ? `<div>Validada: <strong>${fmt(os.validadaEm)}</strong></div>` : ''}
        </div>
      </div>

      <h2>Identificação</h2>
      <div class="grid4">
        <div class="field"><div class="lbl">Nº OS</div><p>${v(os.numeroOS)}</p></div>
        <div class="field"><div class="lbl">Cliente</div><p>${v(os.cliente)}</p></div>
        <div class="field"><div class="lbl">Tipo de Serviço</div><p>${v(os.tipoServico)}</p></div>
        <div class="field"><div class="lbl">Hora</div><p>${v(os.hora)}</p></div>
        <div class="field" style="grid-column:1/-1"><div class="lbl">Técnico Responsável</div><p><strong>${tecnicoNome}</strong></p></div>
      </div>

      <h2>Localização do Trecho</h2>
      <div class="grid4">
        <div class="field" style="grid-column:${endTrecho ? '1/3' : '1/-1'}"><div class="lbl">Endereço do Trecho</div><p>${endTrecho || endOS || '--'}</p></div>
        ${de.cep ? `<div class="field"><div class="lbl">CEP</div><p>${de.cep}</p></div>` : ''}
        ${de.complemento ? `<div class="field"><div class="lbl">Complemento</div><p>${de.complemento}</p></div>` : ''}
        ${de.lat && de.lng
          ? `<div class="field" style="grid-column:1/-1"><div class="lbl">Coordenadas GPS</div>
             <p>Lat: ${Number(de.lat).toFixed(6)} / Lng: ${Number(de.lng).toFixed(6)}</p></div>`
          : (os.lat && os.lng
          ? `<div class="field" style="grid-column:1/-1"><div class="lbl">Coordenadas GPS</div>
             <p>Lat: ${Number(os.lat).toFixed(6)} / Lng: ${Number(os.lng).toFixed(6)}</p></div>`
          : '')}
      </div>

      <h2>Resumo do Trecho</h2>
      <div class="stats-grid">
        <div class="stat-box">
          <div class="stat-num">${totalSings}</div>
          <div class="stat-lbl">Total Singularidades</div>
        </div>
        <div class="stat-box">
          <div class="stat-num">${singsInspecio}</div>
          <div class="stat-lbl">Inspecionadas</div>
        </div>
        <div class="stat-box">
          <div class="stat-num ${singsCobertos > 0 ? 'warn' : ''}">${singsCobertos}</div>
          <div class="stat-lbl">Não Abertas</div>
        </div>
        <div class="stat-box">
          <div class="stat-num">${metragemTotal > 0 ? metragemTotal.toFixed(0) + ' m' : '--'}</div>
          <div class="stat-lbl">Metragem Total</div>
        </div>
        <div class="stat-box">
          <div class="stat-num ${singAlerta > 0 ? 'alert' : ''}">${singAlerta}</div>
          <div class="stat-lbl">Com Pendências</div>
        </div>
        <div class="stat-box">
          <div class="stat-num ${totalOcorr > 0 ? 'alert' : ''}">${totalOcorr}</div>
          <div class="stat-lbl">Ocorrências</div>
        </div>
        <div class="stat-box">
          <div class="stat-num">${totalFotos}</div>
          <div class="stat-lbl">Fotos</div>
        </div>
      </div>

      ${(urlRuas || urlSatelite) ? `
        <h2>Mapa do Trecho</h2>
        <div class="foto-row">
          ${urlRuas ? `
            <div class="foto-item mapa-item">
              <div class="foto-frame mapa-frame"><img src="${urlRuas}" /></div>
              <div class="foto-leg">Mapa de Ruas</div>
            </div>` : ''}
          ${urlSatelite ? `
            <div class="foto-item mapa-item">
              <div class="foto-frame mapa-frame"><img src="${urlSatelite}" /></div>
              <div class="foto-leg">Satélite</div>
            </div>` : ''}
        </div>` : ''}

      ${(fotoEnderecoInspecionado || fotoGoogleMaps) ? `
        <h2>Endereço Inspecionado / Google Maps</h2>
        <div class="foto-row" style="margin-bottom:8px;">
          ${fotoEnderecoInspecionado ? `
            <div class="foto-item">
              <div class="foto-frame"><img src="${fotoEnderecoInspecionado.url}" /></div>
              <div class="foto-leg">Endereço Inspecionado${fotoEnderecoInspecionado.legenda ? ' — ' + fotoEnderecoInspecionado.legenda : ''}</div>
            </div>` : ''}
          ${fotoGoogleMaps ? `
            <div class="foto-item">
              <div class="foto-frame"><img src="${fotoGoogleMaps.url}" /></div>
              <div class="foto-leg">Google Maps${fotoGoogleMaps.legenda ? ' — ' + fotoGoogleMaps.legenda : ''}</div>
            </div>` : ''}
        </div>` : ''}
    </div>`;

  // ── PÁGINA 2: Tabela de Singularidades ────────────────────────────────────
  const tableSings = sings.length === 0
    ? '<p style="color:#a0aec0;text-align:center;padding:20px 0;">Nenhuma singularidade registrada.</p>'
    : `
    <table class="data-table">
      <colgroup>
        <col style="width:4%"/>
        <col style="width:14%"/>
        <col style="width:14%"/>
        <col style="width:9%"/>
        <col style="width:14%"/>
        <col style="width:12%"/>
        <col style="width:16%"/>
        <col style="width:8%"/>
        <col style="width:10%"/>
        <col style="width:13%"/>
      </colgroup>
      <thead>
        <tr>
          <th>#</th>
          <th>Nº do Imóvel /<br/>Identificador</th>
          <th>Sing. Anterior</th>
          <th>Tipo</th>
          <th>Local</th>
          <th>Tráfego</th>
          <th>Resultado</th>
          <th style="text-align:center;">PV<br/>Sujo</th>
          <th style="text-align:center;">Coberto/<br/>Travado</th>
          <th style="text-align:right;">Metrag. à<br/>Ant. (m)</th>
        </tr>
      </thead>
      <tbody>
        ${sings.map((s, i) => {
          const temAlerta =
            (s.condSing     && s.condSing     !== 'Normal') ||
            (s.condBancada  && s.condBancada  !== 'Normal') ||
            (s.condCanaleta && s.condCanaleta !== 'Normal') ||
            (s.resultado?.length > 0 && !(s.resultado.length === 1 && s.resultado[0] === 'Normal'));
          const naAberta = s.pvCoberto || s.pvTravado;
          // Sub-linha: apenas campos com valor fora do Normal
          const isAnomalo = (v) => v && v !== 'Normal' && v !== '';
          const anomalos = [
            isAnomalo(s.tipoPavimento) ? `Pavimento: ${s.tipoPavimento}` : '',
            isAnomalo(s.condSing)      ? `Cond. Sing.: ${s.condSing}`    : '',
            isAnomalo(s.condBancada)   ? `Bancada: ${s.condBancada}`     : '',
            isAnomalo(s.condCanaleta)  ? `Canaleta: ${s.condCanaleta}`   : '',
          ].filter(Boolean).join('  ·  ');
          
          // Metragem adicional (singularidades cruzadas)
          const ramaisInfo = (s.ramaisAdicionais || [])
            .map(r => {
              const fromSing = sings.find(x => x._id === r.paraPVId);
              const fromLabel = fromSing ? `SING ${sings.indexOf(fromSing) + 1}` : '?';
              const metStr = r.metragem ? `${r.metragem}m` : '?';
              const descStr = r.descricao ? ` — ${r.descricao}` : '';
              return `vem de ${fromLabel} (${metStr})${descStr}`;
            })
            .join('  ·  ');
          
          return `<tr class="${temAlerta ? 'row-alert' : ''}${naAberta ? ' row-warn' : ''}">
            <td style="text-align:center;font-weight:700;">${i + 1}</td>
            <td>${v(s.numeroImovel)}</td>
            <td>${getPrevSingLabel(i, s)}</td>
            <td>${v(s.tipoSing)}</td>
            <td>${v(s.localSing)}</td>
            <td>${v(s.trafego)}</td>
            <td class="${(s.resultado?.length > 0 && !(s.resultado.length === 1 && s.resultado[0] === 'Normal')) ? 'cell-alert' : ''}">${Array.isArray(s.resultado) ? s.resultado.join(', ') : (s.resultado || '—')}</td>
            <td style="text-align:center;">${s.pvSujo ? '⚠️' : '—'}</td>
            <td style="text-align:center;">${naAberta ? '🔒' : '—'}</td>
            <td style="text-align:right;">${i === 0 ? '—' : (s.metragemAoPV || '--')}</td>
          </tr>
          ${anomalos ? `<tr class="${temAlerta ? 'row-alert-sub' : 'row-sub'}">
            <td></td>
            <td colspan="9" style="font-size:9px;color:#c53030;font-weight:600;padding:1px 6px 5px;line-height:1.5;">⚠ ${anomalos}</td>
          </tr>` : ''}
          ${naAberta && s.motivoNaoAberto
            ? `<tr class="row-warn-sub"><td></td><td colspan="9" style="font-size:9px;color:#7b341e;padding:1px 6px 5px;"><em>Motivo não aberto: ${s.motivoNaoAberto}</em></td></tr>`
            : ''}
          ${ramaisInfo ? `<tr class="row-sub">
            <td></td>
            <td colspan="9" style="font-size:9px;color:#2d5282;font-weight:600;padding:1px 6px 5px;line-height:1.5;">🔀 Ramais adicionais: ${ramaisInfo}</td>
          </tr>` : ''}`;
        }).join('')}
      </tbody>
    </table>`;

  const pagina2 = `
    <div class="page info-continued">
      <h2>Singularidades — Detalhamento (${sings.length})</h2>
      ${tableSings}
    </div>`;

  // ── PÁGINA 3: Fotos das Singularidades ────────────────────────────────────
  const singsComRegistro = sings.filter(s => s.fotoInicio?.url || s.fotoFim?.url || s.pvCoberto || s.pvTravado);
  const fotosSingsHtml = singsComRegistro.length === 0 ? '' : sings.map((s, idx) => {
    const temFoto = Boolean(s.fotoInicio?.url || s.fotoFim?.url);
    const naAberta = Boolean(s.pvCoberto || s.pvTravado);
    if (!temFoto && !naAberta) return '';
    const textoPadrao = s.pvTravado ? 'PV Travado' : 'PV Coberto';
    return `
      <div class="foto-group">
        <div class="foto-group-title">${v(s.tipoSing)} ${idx + 1} — Identificador ${v(s.numeroImovel)}</div>
        <div class="foto-row">
          ${s.fotoInicio?.url ? `
            <div class="foto-item">
              <div class="foto-frame"><img src="${s.fotoInicio.url}" /></div>
              <div class="foto-leg">Início${s.fotoInicio.legenda ? ' — ' + s.fotoInicio.legenda : ''}</div>
            </div>` : ''}
          ${s.fotoFim?.url ? `
            <div class="foto-item">
              <div class="foto-frame"><img src="${s.fotoFim.url}" /></div>
              <div class="foto-leg">Final${s.fotoFim.legenda ? ' — ' + s.fotoFim.legenda : ''}</div>
            </div>` : ''}
          ${!temFoto && naAberta ? `
            <div class="foto-item">
              <div class="foto-frame foto-empty">${textoPadrao}</div>
              <div class="foto-leg">Sem registro fotográfico</div>
            </div>` : ''}
        </div>
      </div>`;
  }).join('');

  // ── OCORRÊNCIAS ───────────────────────────────────────────────────────────
  const ocorrHtml = ocorrs.length === 0 ? '' : ocorrs.map((o, i) => {
    const label = TIPO_OCC_LABEL[o.tipo] || 'Ocorrência';
    let detalhes = '';
    if (o.tipo === 'galeria') {
      detalhes = `
        <div class="field"><div class="lbl">Existência</div><p>${v(o.galeriaTipo)}</p></div>
        ${o.galeiraPositions?.length
          ? `<div class="field"><div class="lbl">Posição</div><p>${o.galeiraPositions.join(', ')}</p></div>`
          : ''}`;
    } else if (o.tipo === 'imovel') {
      detalhes = `
        <div class="field"><div class="lbl">Nº Imóvel</div><p>${v(o.numeroImovel)}</p></div>
        <div class="field"><div class="lbl">Entre PVs</div><p>${v(o.pvEntreDe)} — ${v(o.pvEntreAte)}</p></div>
        <div class="field"><div class="lbl">Tipo Extravasor</div><p>${v(o.tipoOcorrencia)}${o.tipoOutros ? ' — ' + o.tipoOutros : ''}</p></div>
        ${o.descricao ? `<div class="field" style="grid-column:1/-1"><div class="lbl">Descrição</div><p>${o.descricao}</p></div>` : ''}`;
    }
    const fotosOcorr = (o.fotos || []).filter(f => f.url);
    return `
      <div class="ocorr-block">
        <div class="ocorr-header">
          <span class="ocorr-badge">${i + 1} — ${label}</span>
        </div>
        ${detalhes ? `<div class="grid2" style="margin:10px 0 0;">${detalhes}</div>` : ''}
        ${fotosOcorr.length > 0 ? `
          <div class="foto-row" style="margin-top:10px;">
            ${fotosOcorr.map(f => `
              <div class="foto-item">
                <div class="foto-frame"><img src="${f.url}" /></div>
                ${f.legenda ? `<div class="foto-leg">${f.legenda}</div>` : ''}
              </div>`).join('')}
          </div>` : ''}
      </div>`;
  }).join('');

  // ── Fotos gerais da OS ────────────────────────────────────────────────────
  const fotosOSHtml = fotos_os.filter(f => f.url).length === 0 ? '' :
    `<div class="foto-group">
      <div class="foto-group-title">Fotos Gerais da OS</div>
      <div class="foto-row">
        ${fotos_os.filter(f => f.url).map(f => `
          <div class="foto-item">
            <div class="foto-frame"><img src="${f.url}" /></div>
            ${f.legenda ? `<div class="foto-leg">${f.legenda}</div>` : ''}
          </div>`).join('')}
      </div>
    </div>`;

  // ── Checklist padrão da OS ────────────────────────────────────────────────
  const checklistHtml = CHECKLIST_PADRAO.map(({ id, label }) =>
    `<tr>
      <td style="padding:4px 6px;font-size:14px;width:28px;">${os.checklist?.[id] ? '✅' : '✗'}</td>
      <td style="padding:4px 6px;${os.checklist?.[id] ? '' : 'text-decoration:line-through;color:#a0aec0;'}">${label}</td>
    </tr>`
  ).join('');

  // ── PÁGINA 3+: Fotos + Ocorrências + Conclusão + Validação ───────────────
  const pagina3 = `
    <div class="page">
      ${singsComRegistro.length > 0 ? `
        <h2>Registros Fotográficos — Singularidades (${singsComRegistro.length} itens)</h2>
        ${fotosSingsHtml}` : ''}

      ${ocorrs.length > 0 ? `
        <h2>Ocorrências Identificadas (${ocorrs.length})</h2>
        ${ocorrHtml}` : ''}

      ${fotosOSHtml ? `<h2>Fotos Gerais</h2>${fotosOSHtml}` : ''}

      ${os.observacoes ? `<h2>Nota de Conclusão</h2><p style="white-space:pre-wrap;">${os.observacoes}</p>` : ''}

      ${os.acaoSugestiva ? `<h2>Ação Sugestiva</h2><p style="white-space:pre-wrap;">${os.acaoSugestiva}</p>` : ''}

      ${os.validadorNome ? `
        <h2>Validação</h2>
        <div class="val-box ${os.status === 'aprovada' ? 'ap-box' : 'rp-box'}">
          <strong>${os.status === 'aprovada' ? '✅ Aprovada' : '❌ Reprovada'}</strong>
          por ${os.validadorNome}${os.validadaEm ? ' em ' + fmt(os.validadaEm) : ''}
          ${os.comentarioValidacao ? '<br/><em>' + os.comentarioValidacao + '</em>' : ''}
        </div>` : ''}
    </div>`;

  // ── CSS ───────────────────────────────────────────────────────────────────
  const css = `
    *{box-sizing:border-box;}
    body{font-family:Arial,sans-serif;margin:0;padding:0;font-size:12px;color:#1a202c;}
    .page{padding:32px 40px 32px; page-break-after:auto;}
    .report-pages .page:last-child{page-break-after:auto;}
    h1{font-size:20px;margin:0 0 4px;}
    h2{font-size:13px;font-weight:800;margin:0 0 12px;color:${cor};
      border-bottom:3px solid ${corLight};padding-bottom:8px;padding-top:8px;
      text-transform:uppercase;letter-spacing:.06em;}
    p{line-height:1.7;margin:0 0 6px;color:#2d3748;}
    .page-header{background:#fff;display:flex;align-items:center;justify-content:space-between;
      padding:0 0 10px;border-bottom:2px solid ${cor};margin-bottom:14px;}
    .os-title-bar{display:flex;justify-content:space-between;align-items:flex-start;
      margin-bottom:14px;}
    .info-continued{padding-top:0;}
    .badge{display:inline-block;padding:2px 10px;border-radius:20px;
      font-size:11px;font-weight:700;background:${bc.split(';')[0]};}
    .grid4{display:grid;grid-template-columns:repeat(4,1fr);gap:6px 20px;margin-bottom:8px;}
    .grid2{display:grid;grid-template-columns:1fr 1fr;gap:6px 20px;}
    .field{margin-bottom:6px;}
    .field .lbl{font-size:10px;text-transform:uppercase;font-weight:700;color:#718096;}
    .field p{margin:2px 0 0;}
    /* Stats */
    .stats-grid{display:grid;grid-template-columns:repeat(7,1fr);gap:8px;margin:8px 0;}
    .stat-box{background:#f7fafc;border:1px solid #e2e8f0;border-radius:8px;
      padding:10px 6px;text-align:center;}
    .stat-num{font-size:22px;font-weight:800;color:${cor};}
    .stat-num.warn{color:#d97706;}
    .stat-num.alert{color:#c53030;}
    .stat-lbl{font-size:9px;text-transform:uppercase;color:#718096;margin-top:2px;}
    /* Table */
    .data-table{border-collapse:collapse;width:100%;font-size:9px;table-layout:fixed;}
    .data-table thead{display:table-header-group;}
    .data-table th{background:${cor};color:#fff;padding:4px 5px;text-align:left;
      font-size:8px;font-weight:700;text-transform:uppercase;
      white-space:normal;word-break:break-word;line-height:1.3;}
    .data-table td{padding:3px 5px;border-bottom:1px solid #e2e8f0;vertical-align:middle;
      white-space:normal;word-break:break-word;line-height:1.3;overflow-wrap:break-word;}
    .data-table tr:nth-child(even){background:#f7fafc;}
    .row-alert td{background:#fff5f5!important;}
    .row-warn td{background:#fffbeb!important;}
    .row-sub td{background:#f7fafc!important;border-bottom:1px solid #e2e8f0;}
    .row-alert-sub td{background:#fff5f5!important;border-bottom:1px solid #fed7d7;}
    .row-warn-sub td{background:#fffbeb!important;border-bottom:1px solid #fbd38d;}
    .cell-alert{color:#c53030;font-weight:700;}
    /* Fotos */
    .foto-group{margin-bottom:20px;}
    .foto-group-title{font-size:12px;font-weight:700;color:${cor};
      text-transform:uppercase;margin-bottom:10px;
      padding:6px 0;border-bottom:2px solid ${corLight};}
    .foto-row{display:grid;grid-template-columns:1fr 1fr;gap:12px;align-items:start;}
    .foto-item{min-width:0;text-align:center;break-inside:avoid;page-break-inside:avoid;}
    .foto-frame{width:100%;height:200px;display:flex;align-items:center;justify-content:center;
      border-radius:6px;border:1px solid #e2e8f0;background:#fff;overflow:hidden;}
    .mapa-frame{height:340px;}
    .foto-empty{font-size:12px;font-weight:800;color:#4a5568;background:#edf2f7;
      text-transform:uppercase;letter-spacing:.04em;}
    .foto-item img{width:100%;height:100%;object-fit:cover;display:block;}
    .foto-leg{font-size:10px;color:#718096;margin-top:4px;}
    /* Ocorrências */
    .ocorr-block{border:1px solid #e2e8f0;border-radius:8px;
      padding:12px;margin-bottom:12px;background:#fafafa;
      break-inside:avoid;page-break-inside:avoid;}
    .ocorr-header{margin-bottom:6px;}
    .ocorr-badge{display:inline-block;padding:3px 10px;border-radius:20px;
      font-size:11px;font-weight:700;background:${corLight};color:${cor};}
    /* Validation */
    table{border-collapse:collapse;width:100%;}
    td{vertical-align:middle;}
    .val-box{padding:10px;border-radius:8px;margin-top:6px;}
    .ap-box{background:#f0fff4;border:1px solid #9ae6b4;}
    .rp-box{background:#fff5f5;border:1px solid #feb2b2;}
    /* Rodapé fixo (tela) */
    .page-footer-fixed{position:fixed;bottom:0;left:0;right:0;z-index:999;
      display:flex;justify-content:space-between;align-items:center;
      padding:3px 40px 4px;background:#fff;
      border-top:2px solid #e2e8f0;
      font-size:8px;color:#718096;}
    .footer-brand{text-align:right;line-height:1.4;}
    .footer-brand strong{color:#06b6d4;font-weight:700;}
    .footer-brand .footer-site{display:block;font-size:7px;color:#a0aec0;}
    /* Rodapé impressão — sempre no DOM, apenas invisível na tela.
       Chrome não re-renderiza position:fixed que sai de display:none em @media print,
       por isso usamos visibility:hidden/visible em vez de display:none/flex. */
    .print-footer{
      display:flex;visibility:hidden;
      position:fixed;bottom:3mm;left:0;right:0;z-index:9999;
      justify-content:space-between;align-items:center;
      padding:3px 15mm 4px;
      background:white;border-top:2px solid #e2e8f0;
      font-size:8px;color:#718096;
      -webkit-print-color-adjust:exact;
      print-color-adjust:exact;}
    .print-footer .footer-brand{text-align:right;line-height:1.4;}
    .print-footer .footer-brand strong{color:#06b6d4;font-weight:700;}
    .print-footer .footer-brand .footer-site{display:block;font-size:7px;color:#a0aec0;}
    @page{margin:18mm 15mm 3mm 15mm;}
    @media print{
      .print-actions{display:none!important;}
      .page-footer-fixed{display:none!important;}
      .print-footer{visibility:visible;}
      .info-continued{break-before:page;page-break-before:always;}
      .page{padding:16px 0 16mm;}
      .page-header{padding:0 0 8px;margin-bottom:12px;}
      h2{padding-top:12px;}
      .foto-row{grid-template-columns:1fr 1fr;gap:10px;}
      .foto-frame{width:100%;height:150px;}
      .mapa-frame{height:270px;}
      .ocorr-block,.foto-group,.foto-item{break-inside:avoid;page-break-inside:avoid;}
      .data-table{font-size:8px;}
      .data-table td,.data-table th{padding:2px 4px;}
    }`;

  return `<!DOCTYPE html><html lang="pt-BR"><head><meta charset="UTF-8"/>
    <title>Relatório OS ${v(os.numeroOS)} — ${os.tipoServico || 'Inspeção de Rede'}</title>
    <style>${css}</style>
  </head><body>
    <div class="page-footer-fixed">
      <span>PG Soluções &mdash; Gestão de Serviços</span>
      <span class="footer-brand">Desenvolvido por <strong>SyncWave</strong><span class="footer-site">www.syncwave.com.br</span></span>
    </div>
    <div class="print-footer">
      <span>PG Soluções &mdash; Gestão de Serviços</span>
      <span class="footer-brand">Desenvolvido por <strong>SyncWave</strong><span class="footer-site">www.syncwave.com.br</span></span>
    </div>
    <div class="report-pages">
      ${pagina1}
      ${pagina2}
      ${pagina3}
    </div>
    <div class="print-actions" style="text-align:center;padding:20px 0 60px;">
      <button onclick="window.print()"
        style="padding:10px 32px;background:${cor};color:#fff;border:none;
        border-radius:8px;cursor:pointer;font-size:14px;font-weight:700;">
        🖨️ Imprimir / Salvar PDF
      </button>
    </div>
  </body></html>`;
}
